import React, {isValidElement, useContext, useMemo, useState} from 'react';
import {Outlet, useLocation} from 'react-router-dom';
import {DesktopHud} from 'widgets/hud/ui/DesktopHud.tsx';
import {Content, OutletWrapper, Shell, TitleBar, TitleRight} from './styles';
import {useResize} from 'app/lib/hooks/useResize.ts';
import {BreadcrumbsWrapper} from 'widgets/Layout/ui/Breadcrumbs';
import MobileBottom from '../../hud/ui/MobileBottom.tsx';
import MobileHeader from '../../hud/ui/MobileHeader.tsx';
import {usePageTitleStore} from 'entities/pageTitle';
import {DiaryNewEntrie} from 'features/diary/index.ts';
import {useDiaryStore} from 'entities/diary/index.ts';
import {archivedBlockedPaths, routes} from '../../../app/routes/config.ts';
import {useCustomElements} from 'widgets/Layout/lib/useCustomElemts.tsx';
import {useOutletColorStore} from 'entities/outletColor/model/store.ts';
import {useApiariesStore} from 'entities/apiaries/index.ts';
import {CreateApiary} from 'features/apiary/index.ts';
import {DictionaryEditModal} from 'widgets/dictionaryEditModal';
import {NewDeviceModal} from 'widgets/newDeviceModal';
import {ArchivedApiary} from 'widgets/archivedApiary';
import {SubmitModal} from 'widgets/SubmitModal';
import {archiveApiary, restoreApiary} from 'widgets/dashboard/helpers/requests.ts';
import {useUserStore} from 'entities/user';
import {toast} from 'react-toastify';
import {SearchModal} from 'widgets/searchModal';
import {AddDictionaryModal} from 'features/dictionaries/index.ts';
import {useDictionariesStore} from 'entities/dictionaries/index.ts';
import {EditDictionaryNameModal} from 'features/dictionaries/ui/RenameDicrionaryModal.tsx';
import {HivesNewHive} from '../../../features/hives/ui/hivesNew/HivesNewHive.tsx';
import {useHivesStore} from '../../../entities/hives';
import {VetisAccessDrawers} from 'features/vetis';
import {RenameDeviceModal} from 'features/rename-device-modal/ui/RenameDeviceModal.tsx';
import {useDevicesStore} from 'entities/devices';
import {HiveLocationModal} from 'widgets/hiveLocationModal';
import {ConfigContext} from "../../../app/App.tsx";

function renderSlot(Slot: React.ReactNode | React.ComponentType | null | undefined) {
  if (!Slot) return null;
  if (typeof Slot === 'function') {
    const Comp = Slot as React.ComponentType;
    return <Comp />;
  }
  return isValidElement(Slot) ? Slot : <>{Slot}</>;
}

export const Layout = () => {
  const kind = useResize();
  const routeTitle = usePageTitleStore((s) => s.routeTitle);
  const dynamicTitle = usePageTitleStore((s) => s.dynamicTitle);
  const isNewEntrieOpen = useDiaryStore((s) => s.isNewEntrieOpen);
  const setIsNewEntrieOpen = useDiaryStore((s) => s.setIsNewEntrieOpen);
  const isCreateApiaryOpen = useApiariesStore((s) => s.isModalOpen);
  const deviceModalState = useDevicesStore((s) => s.openState);
  const setDeviceModalState = useDevicesStore((s) => s.setModalState);
  const setIsCreateApiaryOpen = useApiariesStore((s) => s.setIsModalOpen);
  const typeHandlerModalDictionaries = useDictionariesStore((s) => s.typeHandlerType);
  const visibleTitle = useMemo(() => dynamicTitle || routeTitle || '', [dynamicTitle, routeTitle]);
  const {CurrentRightCol, CurrentUpperTitleMobile} = useCustomElements(routes);
  const outletColor = useOutletColorStore((s) => s.currentColor);
  const currentApiary = useApiariesStore((s) => s.currentApiary());
  const getApiaries = useApiariesStore((s) => s.getApiaries);
  const userInfo = useUserStore((s) => s.userInfo);
  const [isOpenArchiveModal, setIsOpenArchiveModal] = useState(false);
  const isDeleted = !!currentApiary?.deletedAt;
  const location = useLocation();
  const isNewOpen = useHivesStore((s) => s.isOpenPop);
  const setIsNewOpen = useHivesStore((s) => s.managePopup);
  const setLocationModalOpen = useHivesStore((s) => s.setLocationModalOpen);
  const appConfig = useContext(ConfigContext);

  const shouldShowArchived =
    isDeleted && archivedBlockedPaths.some((p) => location.pathname.startsWith(p));

  const onArchiveToggle = async () => {
    try {
      let status = 404;
      if (isDeleted) {
        status = await restoreApiary(currentApiary?.id);
      } else {
        status = await archiveApiary(currentApiary?.id);
      }
      if (status >= 200 && status < 300) {
        await getApiaries(userInfo?.id);
      }
    } catch {
      toast.error('Ошибка при изменении статуса пасеки');
    } finally {
      setIsOpenArchiveModal(false);
    }
  };

  const renderOutlet = () =>
    shouldShowArchived ? (
      <>
        <ArchivedApiary onRestore={() => setIsOpenArchiveModal(true)} />
        <SubmitModal
          isOpen={isOpenArchiveModal}
          onClose={() => setIsOpenArchiveModal(false)}
          onConfirm={onArchiveToggle}
          title={'Вы уверены, что хотите вернуть пасеку из архива?'}
          subtitle={''}
        />
      </>
    ) : (
      <Outlet />
    );

  if (!kind.isScreenLg) {
    return (
      <>
        <Content style={{background: outletColor}}>
          <MobileHeader />
          {renderSlot(CurrentUpperTitleMobile)}
          {visibleTitle && (
            <TitleBar>
              {typeof visibleTitle === 'string' || typeof visibleTitle === 'number' ? (
                <h1>{visibleTitle}</h1>
              ) : (
                visibleTitle
              )}
              {CurrentRightCol ? <TitleRight>{renderSlot(CurrentRightCol)}</TitleRight> : null}
            </TitleBar>
          )}
          <OutletWrapper style={{background: outletColor}}>{renderOutlet()}</OutletWrapper>
          <MobileBottom />
        </Content>
        <DiaryNewEntrie
          drawerProps={{
            isOpen: isNewEntrieOpen,
            position: 'right',
            onClose: () => setIsNewEntrieOpen(false),
          }}
          isMobile={!kind.isScreenMd}
        />
        <CreateApiary
          isOpen={isCreateApiaryOpen}
          onClose={() => setIsCreateApiaryOpen(false)}
          position="right"
          isMobile={!kind.isScreenMd}
        />
        <HiveLocationModal
          onClose={() => setLocationModalOpen(false)}
          isMobile={!kind.isScreenMd}
        />
        <RenameDeviceModal
          onClose={() => setDeviceModalState({isOpen: false, name: null, id: null})}
          isMobile={!kind.isScreenMd}
          modalState={deviceModalState}
        />
        <HivesNewHive isOpen={isNewOpen} onClose={() => setIsNewOpen(false)} />
        <DictionaryEditModal
          typeHandler={typeHandlerModalDictionaries}
          isMobile={!kind.isScreenMd}
        />
        <AddDictionaryModal isMobile={!kind.isScreenMd} />
        <EditDictionaryNameModal isMobile={!kind.isScreenMd} />
        <NewDeviceModal />
        <SearchModal />
        <VetisAccessDrawers />
      </>
    );
  }

  return (
    <Shell>
      <aside style={{position: 'relative'}}>
        <DesktopHud />
      </aside>
      <Content style={{background: outletColor}}>
        <BreadcrumbsWrapper config={appConfig} />
        {visibleTitle && (
          <TitleBar>
            {typeof visibleTitle === 'string' || typeof visibleTitle === 'number' ? (
              <h1>{visibleTitle}</h1>
            ) : (
              visibleTitle
            )}
            {CurrentRightCol ? <TitleRight>{renderSlot(CurrentRightCol)}</TitleRight> : null}
          </TitleBar>
        )}
        <OutletWrapper style={{background: outletColor}}>{renderOutlet()}</OutletWrapper>
      </Content>
      <DiaryNewEntrie
        drawerProps={{
          isOpen: isNewEntrieOpen,
          position: 'right',
          onClose: () => setIsNewEntrieOpen(false),
        }}
        isMobile={!kind.isScreenMd}
      />
      <CreateApiary
        isOpen={isCreateApiaryOpen}
        onClose={() => setIsCreateApiaryOpen(false)}
        position="right"
        isMobile={!kind.isScreenMd}
      />
      <HiveLocationModal onClose={() => setLocationModalOpen(false)} isMobile={!kind.isScreenMd} />
      <RenameDeviceModal
        onClose={() => setDeviceModalState({isOpen: false, name: null, id: null})}
        isMobile={!kind.isScreenMd}
        modalState={deviceModalState}
      />
      <HivesNewHive isOpen={isNewOpen} onClose={() => setIsNewOpen(false)} />
      <DictionaryEditModal typeHandler={typeHandlerModalDictionaries} isMobile={!kind.isScreenMd} />
      <AddDictionaryModal isMobile={!kind.isScreenMd} />
      <EditDictionaryNameModal isMobile={!kind.isScreenMd} />
      <NewDeviceModal />
      <VetisAccessDrawers />
    </Shell>
  );
};
